import { keepPreviousData } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import { CostHistoryBillCard } from '../../components/bills/CostHistoryBillCard';
import { CostHistoryTotalCard } from '../../components/bills/CostHistoryTotalCard';
import { RANGE_OPTIONS, type RangeOption } from '../../components/bills/cost-history';
import { EmptyState } from '../../components/common/EmptyState';
import { CardSkeleton } from '../../components/common/Skeleton';
import { trpc } from '../../lib/trpc';
import { usePageTitle } from '../../lib/usePageTitle';

const searchSchema = z.object({
  months: z.coerce.number().int().optional().catch(undefined),
});

export const Route = createFileRoute('/bills/history')({
  validateSearch: searchSchema,
  component: BillCostHistoryPage,
});

function toRange(months: number | undefined): RangeOption {
  return RANGE_OPTIONS.find((option) => option === months) ?? 12;
}

/** Kostnadshistorik: how every fixed bill has developed, period by period. */
function BillCostHistoryPage() {
  usePageTitle('Kostnadshistorik');
  const search = Route.useSearch();
  const navigate = useNavigate();
  const range = toRange(search.months);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const query = trpc.recurringBill.historyAll.useQuery(
    { months: range },
    { retry: false, placeholderData: keepPreviousData },
  );

  const setRange = (months: RangeOption) =>
    void navigate({
      to: '/bills/history',
      search: { months: months === 12 ? undefined : months },
      replace: true,
    });

  const data = query.data;
  const bills = data?.bills ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <header>
        <Link
          to="/bills"
          className="-ml-1 inline-flex min-h-11 items-center gap-0.5 text-sm font-medium text-muted-foreground hover:text-foreground sm:min-h-0"
        >
          <ChevronLeft className="size-4" aria-hidden />
          Räkningar
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight">Kostnadshistorik</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Hur dina fasta räkningar har utvecklats, löneperiod för löneperiod.
        </p>
      </header>

      {query.error ? (
        <EmptyState
          icon="⚠️"
          title="Kunde inte hämta historiken"
          description="Försök igen om en liten stund."
        />
      ) : !query.isLoading && bills.length === 0 ? (
        <EmptyState
          icon="🧾"
          title="Inga räkningar än"
          description="Skapa räkningar och koppla regler så visas kostnaderna över tid här."
          action={{ label: 'Gå till Räkningar', onClick: () => void navigate({ to: '/bills' }) }}
        />
      ) : (
        <>
          <CostHistoryTotalCard
            data={data}
            isLoading={query.isLoading}
            range={range}
            onRangeChange={setRange}
          />

          <section aria-labelledby="bill-history-heading" className="space-y-2">
            <div className="flex items-baseline justify-between gap-3 px-1">
              <h2 id="bill-history-heading" className="text-[15px] font-semibold">
                Per räkning
              </h2>
              {data && <p className="text-xs text-muted-foreground">Sorterat på senaste belopp</p>}
            </div>
            {query.isLoading || !data ? (
              <div className="space-y-2">
                <CardSkeleton lines={2} />
                <CardSkeleton lines={2} />
                <CardSkeleton lines={2} />
              </div>
            ) : (
              <ul className="space-y-2">
                {bills.map((bill) => (
                  <CostHistoryBillCard
                    key={bill.id}
                    bill={bill}
                    periods={data.periods}
                    months={range}
                    expanded={expandedId === bill.id}
                    onToggle={() =>
                      setExpandedId((current) => (current === bill.id ? null : bill.id))
                    }
                  />
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
